import { useRef } from 'react';
import { LockUserRequest, User } from 'services/requests';
import { useLockIraUser } from 'hooks/api/iraUser';
import {
  StatusUserFieldNames,
  useKeyboardByForm,
  useStatusUserLockModalForm,
} from 'hooks/forms';
import { useLockIndependentIraUser } from 'hooks/api/independentIra';
import { checkUser } from 'utils';
import { USER_TYPE } from 'constants/userTypes';
import { Modal, ModalProps } from 'components/Modal';
import Button from 'components/Button';
import Textarea from 'components/Textarea';
import messages from './StatusUserLockModal.messages';
import styles from './StatusUserLockModal.module.css';

type Props<T> = {
  user?: T;
  userType: string;
  onRequestClose: VoidFunction;
} & ModalProps;

const StatusUserLockModal = <T extends User>({
  onRequestClose,
  user,
  userType,
  ...restOfProps
}: Props<T>) => {
  const form = useStatusUserLockModalForm();

  const { mutateAsync: lockIraUser, isLoading: isLockIraUserLoading } = useLockIraUser();
  const {
    mutateAsync: lockIndependentIraUser,
    isLoading: isLockIndependentIraUserLoading,
  } = useLockIndependentIraUser();

  const isLockLoading = isLockIraUserLoading || isLockIndependentIraUserLoading;

  const onSubmit = async (): Promise<void> => {
    const payload = form.getValues();

    if (checkUser(user))
      switch (userType) {
        case USER_TYPE.iraUser:
          await lockIraUser({ id: user.id.toString(), payload });
          break;
        case USER_TYPE.independentIra:
          await lockIndependentIraUser({ id: user.id.toString(), payload });
          break;
      }
    onRequestClose();
    form.reset();
  };

  const formRef = useRef<HTMLFormElement>(null);
  useKeyboardByForm(formRef.current, onSubmit);

  const userName = `${user?.firstName} ${user?.middleName ?? ''} ${user?.lastName}`;

  return (
    <Modal
      {...restOfProps}
      onRequestClose={onRequestClose}
      className={styles.statusUserLockModal}
    >
      <h3 className={styles.statusUserLockModal__title}>{messages.title}</h3>
      <p className={styles.statusUserLockModal__subTitle}>
        {messages.subTitleStart}
        {userName}
        {messages.subTitleEnd}
      </p>

      <form onSubmit={form.handleSubmit(onSubmit)} ref={formRef}>
        <Textarea<LockUserRequest>
          form={form}
          name={StatusUserFieldNames.comment}
          className={styles.statusUserLockModal__textarea}
          placeholder={messages.placeholder}
        />
        <div className={styles.statusUserLockModal__buttons}>
          <Button variant={'outline'} onClick={onRequestClose}>
            {messages.buttonCancel}
          </Button>
          <Button type={'submit'} isLoading={isLockLoading}>
            {messages.buttonSubmit}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default StatusUserLockModal;
